import { useState, useEffect, useContext, createContext } from "react";
import api from "@shared/server/api";
import { logger } from "../functions/logger";
import { localStorageAuthv2, localStorageRememberUser } from "../config";

const authContext = createContext();
// Provider component that wraps your app and makes auth object
// ... available to any child component that calls useAuth()
export function ProvideAuth({ children }) {
  const auth = useProvideAuth();

  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

// Hook for child components to get the auth object
// .. and re-render when it changes
export const useAuth = () => {
  return useContext(authContext);
};

// Provider hook that creates auth object and handles state
function useProvideAuth() {
  const initialValue = { user: {}, jwt: null };
  const [data, setData] = useState(initialValue);
  const [errors, setErrors] = useState(null);
  const [isLoading, setLoading] = useState(true);

  // Restoring the session at startup is where "the app logged me out" is decided,
  // so every outcome other than the healthy one leaves a record.
  //
  // The JSON.parse is guarded because it used to be the worst failure in the app:
  // a corrupt `cobro_auth` threw here, which skipped setLoading(false) as well, so
  // isLoading stayed true forever and ProtectedRoute rendered the loading screen
  // for good — no login form, no error, nothing to report. Now it logs and falls
  // back to logged-out, so the user at least reaches the login form.
  useEffect(() => {
    if (data.jwt === null) {
      const localUser = localStorage.getItem(localStorageAuthv2);
      // const localCommon = localStorage.getItem(localStorageCommon);

      if (localUser) {
        let user = null;

        try {
          user = JSON.parse(localUser);
          // const common = JSON.parse(localCommon);
        } catch (e) {
          logger.error("auth.stored_auth_unreadable", "Stored auth is not valid JSON; treating as logged out", {
            entity: "auth",
            context: { error: String(e?.message || e), length: localUser.length },
          });
        }

        if (user && user.jwt != null) {
          setData(user);
        } else {
          // Present but unusable. `action` is the tell: useAuth.login() persists
          // the login response even when it is a routeDisabled reply, which has
          // no jwt — that object then poisons every later startup.
          if (user) {
            logger.warning("auth.stored_auth_without_jwt", "Stored auth has no jwt; treating as logged out", {
              entity: "auth",
              context: { action: user.action ?? null, keys: Object.keys(user).slice(0, 12) },
            });
          }

          setData(initialValue);
        }
      } else {
        setData(initialValue);
      }
    }

    setLoading(false);
  }, []);

  async function login(postData) {
    setErrors(null);
    setLoading(true);

    try {
      const ipAddress = "1.0.0.27";
      const { data: user } = await api.post("/cobro/login/v2", {
        data: { ...postData, ipAddress },
        foceAppUpdateNumber: import.meta.env.VITE_foceAppUpdateNumber,
      });

      if (user.action !== "routeDisabled") {
        setData(user);
      }

      localStorage.setItem(localStorageAuthv2, JSON.stringify(user));

      setLoading(false);

      return user;
    } catch (error) {
      setLoading(false);
      if (error.response) {
        setErrors(error.response.data.message);
      } else {
        setErrors(error.message);
      }
    }
  }

  function logout() {
    localStorage.removeItem(localStorageAuthv2);
    setData(initialValue);
  }

  function setAuthData(data) {
    localStorage.setItem(localStorageAuthv2, JSON.stringify(data));

    setData(data);
  }

  function setRememberData({ username, firstname, user_id }) {
    const deviceObj = { username, firstname: firstname, user_id };
    localStorage.setItem(localStorageRememberUser, JSON.stringify(deviceObj));
  }

  // Return the user object and auth methods
  return { data, errors, isLoading, setRememberData, login, logout, setAuthData };
}
